import { KeetaIntegrationAlertModal } from '@components/Modals/Keeta/keeta-integration-alert-modal'
import { ModalIfoodNewOrders } from '@components/Modals/ifood'
import { useSocketIo } from '@hooks/useSocketIo'
import { type RunMessageType, useWhatsAppBot } from '@hooks/useWhatsAppBot'
import { apiRoute } from '@utils/wm-functions'
import EventEmitter from 'events'
import { Session } from 'next-auth'
import { signOut, useSession } from 'next-auth/react'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import Motoboy, {
  type MotoboyTrackfyPendencies,
  type MotoboyType,
} from '@whatsmenu/entities/dist/motoboy'
import { api, keetaIntegratorApi, nineNineIntegratorApi } from 'src/lib/axios'
import { useFetch } from '../hooks/useFetch'
import { Subscription, useWebSocket } from '../hooks/useWebSocket'
import {
  addItemCartAction,
  addItemPackageCart,
  setCartAction,
  setCartsAction,
  setPackageCartsAction,
} from '../reducers/carts/actions'
import {
  CartsState,
  PackageCartsData,
  cartsReducer,
} from '../reducers/carts/reducer'
import cart, { CartType } from '@whatsmenu/entities/dist/cart'
import type { NineNineOrderInfoType } from '@whatsmenu/entities/src/cart'
import Cart from '../types/cart'
import { IfoodOrderType } from '@whatsmenu/entities/dist/ifood-order'
import { AppContext, ChangeHistory, type RunnerLocationTransmitPayload } from './app.ctx'
import { DateTime } from 'luxon'
import { AgilizoneService } from '@whatsmenu/agilizone'
import { useTransmit } from './transmit.ctx'
import { KeetaOrderModal } from '@components/Modals/Keeta/keeta-order-modal'
import { NineNineOrderModal } from '@components/Modals/NineNine/nine-nine-order-modal'

import { authConfig, createOrder } from "@whatsmenu/entregasexpressas";
import i18next from 'i18next'
import i18n from '@whatsmenu/i18n'


interface CartContextData {
  carts: Cart[]
  packageCarts: PackageCartsData | null
  setCarts: (carts: Cart[]) => void
  setCart: (cart: Cart) => void
  createRun: ({ motoboyId, cartId }: { motoboyId: number; cartId: number }) => Promise<{ id: number; status?: string; createdNewRun?: boolean } | undefined>
  updateMotoboyId: (cartId: number, cartOrigin: string, motoboyId: number, session: Session) => void
  setPackageCarts: (data: PackageCartsData) => void
  showLostRequestsModal: boolean
  setShowLostRequestsModal: Dispatch<SetStateAction<boolean>>
  motoboys: any[]
  setMotoboys: Dispatch<SetStateAction<any[]>>
  cartEvents: EventEmitter
  ifoodOrders: {
    orders: any[]
  }
  keetaOrders: any[]
  setKeetaOrders: Dispatch<SetStateAction<Cart[]>>
  setIfoodOrders: Dispatch<
    SetStateAction<{
      orders: any[]
    }>
  >
  nineNineOrders: any[]
  setNineNineOrders: Dispatch<SetStateAction<any[]>>
  modalNewPlacedIfood: boolean
  setModalNewPlacedIfood: Dispatch<SetStateAction<boolean>>
  newPlacedOrders: {
    orders: any[]
  }
  setNewPlacedOrders: Dispatch<
    SetStateAction<{
      orders: any[]
    }>
  >
  sendToMotoboy: boolean
  setSendToMotoboy: Dispatch<SetStateAction<boolean>>
  createOrderAgilizone: (args: { cart: Cart }) => Promise<void>
  checkAvailabilityMotoboyIfood: (args: { cart: Cart }) => Promise<void>
  createOrderEntregasExpressas: (args: { cart: Cart }) => Promise<{ result: any }>
  sendAppLinkToMotoboy: ( motoboy: { name: string; whatsapp?: string }) => void
  trackfyPendencies: MotoboyTrackfyPendencies[]
  hasTrackfyPendencies: boolean
  getMotoboyTrackfyPendencies: (motoboy: MotoboyType | Motoboy) => MotoboyTrackfyPendencies
  hasMotoboyTrackfyPendencies: (motoboy?: MotoboyType | Motoboy | null) => boolean
}

interface CartProviderProps {
  children: ReactNode
}

export const CartsContext = createContext<CartContextData>(
  {} as CartContextData
)

/** Link do app de entregadores (APK) */
const MOTOBOY_APP_LINK =
  'https://github.com/Grove-Company/.github/releases/download/Trackfy-v1.0.4/application-d8bc2ccc-536e-4e60-b03c-6b10ec8698cf-11-9.apk'

export function CartsProvider({ children }: CartProviderProps) {
  const { wsConnect, wsSubscribe } = useWebSocket()
  const {
    profile,
    setWsPrint,
    verifyInventory,
    setRequestsToPrint,
    audio,
    handleShowToast,
    runnerLocationHandlerRef,
    possibleMobile,
    setSocketCommands,
    setFinishCommand,
    defaultDomain,
  } = useContext(AppContext)
  const { data: session } = useSession()
  const { onCart, sendCashbackMessage, cartDistance, sendMessage, sendMessageFromRun } = useWhatsAppBot()
  const cartEvents = new EventEmitter()
  const { socketIoConnect, socketIoSubscribe, socket, connect } = useSocketIo()
  const { createConnection, connections, createSubscription } = useTransmit()

  const [showLostRequestsModal, setShowLostRequestsModal] = useState(false)
  const [state, dispatch] = useReducer(cartsReducer, {
    carts: [],
    packageCarts: null,
  } as CartsState)
  const cartsRef = useRef<Cart[]>([])
  const runMessagesInFlightRef = useRef(new Set<string>())
  const runMessagesSentAtRef = useRef(
    new Map<number, Partial<Record<RunMessageType, string>>>()
  )
  const [motoboys, setMotoboys] = useState<any[]>([])
  const { data: result_carts } = useFetch<{ carts: CartType[] }>(
    '/dashboard/carts'
  )
  const { data: result_motoboys } = useFetch<{ motoboys: any[] }>(
    '/dashboard/motoboys'
  )
  const { data: result_package_data } = useFetch<{
    packageCarts: PackageCartsData
  }>(`/dashboard/carts/package?page=${1}`)
  const [ifoodOrders, setIfoodOrders] = useState<{ orders: any[] }>({
    orders: [],
  })
  const [keetaOrders, setKeetaOrders] = useState<any[]>([])
  const [nineNineOrders, setNineNineOrders] = useState<any[]>([])
  const [modalNewPlacedIfood, setModalNewPlacedIfood] = useState(false)
  const [newPlacedOrders, setNewPlacedOrders] = useState<{ orders: any[] }>({
    orders: [],
  })

  const [printSubscription, setPrintSubscription] = useState<Subscription>()
  const [sendToMotoboy, setSendToMotoboy] = useState<boolean>(false)
  const [showKeetaModal, setShowKeetaModal] = useState(false)

  /**
   * Cria um pedido na Agilizone a partir do cart informado.
   * Caso o pedido seja criado com sucesso, atualiza o cart
   * com o status de "em rota" e seta o motoboy selecionado
   * como "agilizone".
   *
   * @param {Cart} cart - Cart a ser criado na Agilizone.
   */
  const createOrderAgilizone = async ({ cart }: { cart: Cart }) => {
    if (!cart.address.latitude || !cart.address.longitude) {
      return handleShowToast({
        type: 'erro',
        content: 'Sem coordenadas para enviar para Agilizone',
      })
    }
    try {
      const { data } = await api.post(
        `/dashboard/agilizone/createOrder/${cart.id}`
      )
      data.cart.created_at = cart.created_at
      const newcart = new Cart({ ...cart, ...data.cart })
      setCart(newcart)
      handleShowToast({
        type: 'success',
        content: 'Pedido enviado para Agilizone!',
      })
    } catch (error) {
      handleShowToast({
        type: 'erro',
        content: 'Erro ao chamar a Agilizone',
      })
      throw error
    }
  }

  const createOrderEntregasExpressas = async ({ cart }: { cart: Cart }) => {
    if (!profile?.options?.integrations?.entregasexpressas) {
      throw new Error('Integração com Entregas Expressas não configurada')
    }
    authConfig(profile?.options?.integrations?.entregasexpressas?.api_key)
    if (!cart.address.latitude || !cart.address.longitude) {
      handleShowToast({
        type: 'alert',
        content: 'Sem coordenadas para enviar para Entregas Expressas',
      })
      return { result: null }
    }
    try { //
      const { data } = await createOrder({
        external_id: cart.id.toString(),
        metodo_pagamento: profile?.options?.integrations?.entregasexpressas?.metodo_pagamento,
        categoria_id: 3,
        tipo_veiculo_id: 1,
        cliente_id: profile?.options?.integrations?.entregasexpressas?.client.id,
        endereco_coleta: {
            endereco: `${profile.address.street}, ${profile.address.number} - ${profile.address.neigborhood}, ${profile.address.city} - ${profile.address.state}, ${profile.address.zipcode}`,
            nome: profile.name,
            telefone: profile.whatsapp,
            external_id: profile.id.toString(),
            latitude: profile.address.latitude,
            longitude: profile.address.longitude,
          },
          enderecos_entrega: [
            {
              endereco: `${cart.address.street}, ${cart.address.number} - ${cart.address.neighborhood}, ${cart.address.city} - ${cart.address.uf}, ${cart.address.zipcode}`,
              nome: cart.client.name,
              telefone: cart.client.whatsapp,
              external_id: cart.address.id.toString(),
              latitude: cart.address.latitude,
              longitude: cart.address.longitude,
              order_display_id: `WM-${cart.code}-${cart.type}`
            },
          ],
          prova_entrega_necessaria: false,
          retorno_necessario: false,
      })

      setCart(new Cart({ ...cart, controls: { ...cart.controls, entregas_expressas: data } }))

      return {
        result: data
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  /**
   * Verifica se o estoque de motoboys pode suprir as quantidades desejadas no carrinho.
   *
   * @param {CartType} cart - Carrinho a ser verificado.
   * @return {Promise<void>} Uma promessa que resolve quando a verificação terminar.
   * @throws {Error} Se ocorrer um erro durante a verificação.
   */
  const checkAvailabilityMotoboyIfood = async ({ cart }: { cart: CartType }) => {
    try {
      let cartAvailable: any = null
      // if (!cart.address.zipcode) {
      //   handleShowToast({ type: 'erro', title: 'Disponibilidade', content: 'Esse pedido precisa de CEP' })
      //   return
      // }
      if (cart.origin === 'ifood') {
        handleShowToast({ type: 'alert', title: 'Disponibilidade', content: 'Não é possível chamar um motoboy para esse pedido' })
        return
        // const { data } = await api.get(`/dashboard/ifood/${profile.id}/${cart.origin}/${cart.orderId}/deliveryAvailabilities`)
        // cartAvailable = data.cart
      }

      if (cart.origin === 'whatsmenu') {
        const { data } = await api.get(`/dashboard/ifood/${profile.id}/${cart.origin}/${cart.id}/deliveryAvailabilities`)
        cartAvailable = data.cart
        handleShowToast({ type: 'success', title: 'Localização', content: 'Disponibilidade verificada com sucesso' })
      }

      if (cartAvailable) {
        if (cart.origin === 'whatsmenu') {
          const { data } = await api.get(`/dashboard/ifood/${profile.id}/${cart.origin}/${cart.id}/requestDriver`)

          cartAvailable = data.cart
          const cartFind = state.carts.find((cart) => cart.id === cartAvailable.id)
          cartAvailable.created_at = cartFind?.created_at
          const newCart = new Cart({ ...cartFind, ...cartAvailable })
          setCart(newCart)
          handleShowToast({ type: 'success', title: 'Localização', content: 'Motoboy chamado com sucesso' })
        }
        // if (cart.origin === 'ifood') {
        //   handleShowToast({ type: 'alert', title: 'Disponibilidade', content: 'Não é possível chamar um motoboy para esse pedido' })
        //   return
        //   const { data } = await api.get(`/dashboard/ifood/${profile.id}/${cart.origin}/${cart.orderId}/requestDriver`)
        //   cartAvailable = data.cart
        // }
      }

    } catch (error) {
      handleShowToast({ type: 'erro', title: 'Disponibilidade', content: 'Esse pedido está fora de cobertura' })
      throw error
    }
  }

  socket.on('connect', () => {
    socketIoSubscribe('ifood', profile.slug)
  })

  const setCarts = (carts: Cart[]) => {
    dispatch(setCartsAction(carts))
  }

  const setCart = (cart: Cart) => {
    dispatch(setCartAction(cart))
  }

  const setPackageCarts = (data: PackageCartsData) => {
    dispatch(setPackageCartsAction(data))
  }

  const addItemCart = (cart: CartType) => {
    if (cart.type !== 'P') {
      dispatch(addItemCartAction(cart))
    } else {
      dispatch(addItemPackageCart(cart))
    }
  }

  const updateMotoboyId = async (
    cartId: number,
    cartOrigin: string,
    motoboyId: number,
    session: Session
  ) => {
    try {
      let cartUpdated: any = null
      if (!cartOrigin) {
        cartOrigin = 'whatsmenu'
      }
      switch (cartOrigin) {
        case 'ifood':
          const { data: dataIfood } = await api.patch(`/dashboard/carts/${cartId}/signMotoboyForIntegrator`, {
            motoboyId,
            cartOrigin
          })

          cartUpdated = Cart.fromIfood({order:dataIfood.order, profile})
          break
        case 'keeta':
          const { data: dataKeeta } = await api.patch(`/dashboard/carts/${cartId}/signMotoboyForIntegrator`, {
            motoboyId,
            cartOrigin
          })
          cartUpdated = Cart.fromKeeta({order:dataKeeta.order, profile: dataKeeta.order.merchant, customer: dataKeeta.order.customer, items: dataKeeta.order.items})
          break
        case 'nineNine':
          const { data: dataNineNine } = await api.patch(`/dashboard/carts/${cartId}/signMotoboyForIntegrator`, {
            motoboyId,
            cartOrigin
          })
          cartUpdated = Cart.fromNineNine({order:dataNineNine.order, profile, customer: dataNineNine.order.customer, items: dataNineNine.order.items})
          break
        case 'whatsmenu':
        default:
          const { data: cart } = await api.patch(
            `/dashboard/carts/${cartId}/signMotoboy`,
            { cartId, motoboyId }
          )
          cartUpdated = cart
          break
      }

      const cart = state.carts.find(
        (cart: CartType) => {
          if (cartOrigin === 'ifood') {
          cart.orderId === cartUpdated.orderId
          }
          if (cartOrigin === 'whatsmenu') {
            cart.id === cartUpdated.cart.id
          }
        }
      )
      if (cart) {
        const cartMerged = {
          ...cart,
          motoboyId: cartUpdated.cart.motoboyId,
          motoboy: cartUpdated.motoboy,
        }
        setCart(new Cart(cartMerged))
      }
      handleShowToast({
        type: 'success',
        title: 'Motoboy',
        content: `${i18next.t('delivery_person_signed_successfully')}`,
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const createRun = async ({
    motoboyId,
    cartId,
  }: {
    motoboyId: number
    cartId: number
  }): Promise<{ id: number; status?: string; createdNewRun?: boolean } | undefined> => {
    try {
      const { data: run } = await api.post('/api/v3/motoboy/run', {
        motoboyId,
        cartId,
      })

      const cart = state.carts.find((c: CartType) => c.id === cartId)
      if (cart) {
        const motoboy = motoboys.find((m) => m.id === motoboyId)
        const { run_id: _runId, ...cartWithoutRunId } = cart
        const cartMerged = {
          ...cartWithoutRunId,
          run_id: run.id,
          run: { id: run.id, status: run.status },
          motoboyId,
          motoboy,
        }
        const updatedCart = new Cart(cartMerged)

        // Atualizar o array completo de carts para forçar recomputação
        const updatedCarts = state.carts.map((c: CartType) =>
          c.id === cartId ? updatedCart : new Cart(c)
        )
        setCarts(updatedCarts)
      }

      return run
    } catch (error) {
      console.error('Erro ao criar run:', error)
      throw error
    }
  }

  const trackfyPendencies = useMemo(
    () => Motoboy.listTrackfyPendencies(motoboys),
    [motoboys]
  )
  const hasTrackfyPendencies =
    Boolean(profile?.options?.integrations?.router) && trackfyPendencies.length > 0
  const getMotoboyTrackfyPendencies = Motoboy.getTrackfyPendencies
  const hasMotoboyTrackfyPendencies = Motoboy.hasTrackfyPendencies

  const sendAppLinkToMotoboy = (motoboy: { name: string; whatsapp?: string }) => {
      const phoneDigits = motoboy.whatsapp?.replace(/\D/g, '')
      if (!phoneDigits) {
        return alert('Entregador sem WhatsApp cadastrado.')
      }

      const message = encodeURIComponent(
        `Olá ${motoboy.name}! Baixe o app Trackfy para receber suas corridas:\n${MOTOBOY_APP_LINK}`
      )
      const fullPhone = `${i18n.t('ddi')}${phoneDigits}`
      const { linkWhatsapp, whatsappOficial } = profile.options ?? {}
      const isWindows = navigator.userAgent.includes('Windows')
      const sendMobile = linkWhatsapp === true || linkWhatsapp === undefined

      if ((whatsappOficial || possibleMobile) && sendMobile && !isWindows) {
        window.open(`whatsapp://send?phone=${fullPhone}&text=${message}`)
        return
      }

      if (!('isElectron' in window)) {
        window.open(`https://wa.me/send?phone=${fullPhone}&text=${message}`)
        return
      }

      sendMessage(
        fullPhone,
        `Olá ${motoboy.name}! Baixe o app WhatsMenu Entregador para receber suas corridas:\n${MOTOBOY_APP_LINK}`
      )
      handleShowToast({
        type: 'success',
        title: 'Entregador',
        content: 'Mensagem enviada pelo WhatsApp.',
      })
    }


  const onWsReconnect = async () => {
    const { data: result_carts } = await apiRoute('/dashboard/carts', session)
    const { data: result_package_data } = await apiRoute(
      `/dashboard/carts/package?page=${1}`,
      session
    )
    if (result_carts) {
      const { carts } = result_carts
      setCarts(carts.map((cart: CartType) => new Cart(cart)))
      setShowLostRequestsModal(carts.length > 3)
    }

    if (result_package_data) {
      const { packageCarts } = result_package_data
      setPackageCarts(packageCarts)
    }
  }

  useEffect(() => {
    cartsRef.current = state.carts
  }, [state.carts])

  const haversine = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const toRad = (value: number) => (value * Math.PI) / 180
    const R = 6371
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  const isRunnerStartedEvent = (event: unknown) =>
    typeof event === 'string' && event.toLowerCase() === 'runner_started'

  const isDeliveredEvent = (event: unknown) =>
    typeof event === 'string' && event.toUpperCase() === 'DELIVERED'

  const parseRunnerLocationMessage = (message: unknown) => {
    let parsed: any = message
    if (typeof message === 'string') {
      try {
        parsed = JSON.parse(message)
      } catch {
        return null
      }
    }

    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.event && parsed.data && parsed.latitude === undefined && !isRunnerStartedEvent(parsed.event) && !isDeliveredEvent(parsed.event)) {
      return null
    }

    const runnerHistory = parsed.runner_history ?? parsed.runnerHistory
    if (Array.isArray(runnerHistory)) {
      const hasRunnerStarted = runnerHistory.some((entry: { event?: string }) =>
        isRunnerStartedEvent(entry?.event)
      )
      if (hasRunnerStarted) {
        return {
          event: 'runner_started',
          timestamp: runnerHistory[runnerHistory.length - 1]?.timestamp,
          carts: parsed.carts,
        } as RunnerLocationTransmitPayload
      }
    }

    if (isRunnerStartedEvent(parsed.event) || isDeliveredEvent(parsed.event)) {
      return parsed as RunnerLocationTransmitPayload
    }
    if (parsed.latitude === undefined || parsed.longitude === undefined) return null

    return parsed as RunnerLocationTransmitPayload
  }

  const getRunnerHistory = (cart: Cart): any[] => {
    const history = cart.run?.runnerHistory ?? cart.run?.runner_history
    if (Array.isArray(history)) return history
    if (typeof history !== 'string') return []

    try {
      const parsed = JSON.parse(history)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  const handleRunnerLocationUpdate = (location: RunnerLocationTransmitPayload) => {
    const incomingCarts = location.carts ?? []
    const incomingById = new Map(incomingCarts.map((cart) => [cart.id, cart]))
    const cartIds = new Set(incomingCarts.map((cart) => cart.id))
    const runIds = new Set(
      incomingCarts
        .map((cart) => cart.runId ?? cart.run_id)
        .filter((runId): runId is number => runId != null)
    )

    const runnerStarted = isRunnerStartedEvent(location.event)
    const deliveredEvent = isDeliveredEvent(location.event)
    const changeHistoryPayload = mergeChangeHistoryWithSentMarks(
      location.change_history ?? []
    )

    processRunMessages(changeHistoryPayload, location)

    const historyEntry: Record<string, string> = {
      timestamp: location.timestamp ?? new Date().toISOString(),
      receivedAt: new Date().toISOString(),
      event: location.event ?? 'RUNNING',
    }

    if (location.latitude !== undefined && location.longitude !== undefined) {
      historyEntry.latitude = String(location.latitude)
      historyEntry.longitude = String(location.longitude)
    }

    let hasChanges = false
    const updatedCarts = cartsRef.current.map((cart) => {
      const matchesById = cartIds.has(cart.id)
      const matchesByRun = cart.run_id != null && runIds.has(cart.run_id)
      if (!matchesById && !matchesByRun) return cart

      const existingHistory = getRunnerHistory(cart)
      if (
        runnerStarted &&
        existingHistory.some((entry) => isRunnerStartedEvent(entry?.event))
      ) {
        return cart
      }

      const incoming = incomingById.get(cart.id)
      const nextStatus =
        incoming?.status ??
        (deliveredEvent && matchesById ? 'delivered' : cart.status)

      const updatedHistory = [...existingHistory, historyEntry]

      hasChanges = true
      return new Cart({
        ...cart,
        status: nextStatus,
        run: {
          ...cart.run,
          runnerHistory: updatedHistory,
          runner_history: updatedHistory,
        },
      })
    })

    if (hasChanges) {
      setCarts(updatedCarts)
    }

    if (location.latitude === undefined || location.longitude === undefined) return

    for (const locationCart of location.carts ?? []) {
      if (!locationCart.address?.latitude || !locationCart.address?.longitude) continue

      const distance = haversine(
        Number(locationCart.address.latitude),
        Number(locationCart.address.longitude),
        Number(location.latitude),
        Number(location.longitude)
      )
      const cart =
        cartsRef.current.find((currentCart) => currentCart.id === locationCart.id) ??
        locationCart
      const changeHistory = changeHistoryPayload.find(
        (history) => history.cartId === locationCart.id
      )
      // PENDING só recebe message_start; arriving/arrived só com RUNNING
      if (cart.client && changeHistory?.status === 'RUNNING') {
        if (
          distance <= 0.6 &&
          distance > 0.2 &&
          !isRunMessageFilled(changeHistory.message_arriving_on_location) &&
          claimRunMessage(locationCart.id, 'message_arriving_on_location')
        ) {
          cartDistance(locationCart.id, cart.client, changeHistory, distance)
        } else if (
          distance <= 0.2 &&
          !isRunMessageFilled(changeHistory.message_arrived_on_location) &&
          claimRunMessage(locationCart.id, 'message_arrived_on_location')
        ) {
          cartDistance(locationCart.id, cart.client, changeHistory, distance)
        }
      }
    }
  }


  function getCartStatusUrl(cartCode: string | number): string {
    const url = !defaultDomain?.includes('whatsmenu')
      ? `https://${defaultDomain}`
      : i18n.language === 'pt-BR'
        ? `${process.env.NEXT_PUBLIC_WHATSMENU_BASE_URL}/${profile.slug}`
        : `https://whatsmenu.pro/${profile.slug}`

    return `${url.trim()}/status/${cartCode}`
  }

  function isRunMessageFilled(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim().length > 0
  }

  function rememberRunMessageSent(cartId: number, type: RunMessageType, sentAt?: string): void {
    const markedAt = sentAt || DateTime.now().toISO() || new Date().toISOString()
    const current = runMessagesSentAtRef.current.get(cartId) ?? {}
    runMessagesSentAtRef.current.set(cartId, {
      ...current,
      [type]: markedAt,
    })
  }

  function releaseRunMessageClaim(cartId: number, type: RunMessageType): void {
    runMessagesInFlightRef.current.delete(`${cartId}:${type}`)
    const current = runMessagesSentAtRef.current.get(cartId)
    if (!current) return
    const { [type]: _, ...rest } = current
    runMessagesSentAtRef.current.set(cartId, rest)
  }

  /** Reserva o envio/marcação. Retorna false se já foi enviado ou está em voo. */
  function claimRunMessage(cartId: number, type: RunMessageType): boolean {
    if (isRunMessageFilled(runMessagesSentAtRef.current.get(cartId)?.[type])) {
      return false
    }
    const messageKey = `${cartId}:${type}`
    if (runMessagesInFlightRef.current.has(messageKey)) {
      return false
    }
    runMessagesInFlightRef.current.add(messageKey)
    rememberRunMessageSent(cartId, type)
    return true
  }

  function mergeChangeHistoryWithSentMarks(histories: ChangeHistory[]): ChangeHistory[] {
    for (const history of histories) {
      if (isRunMessageFilled(history.message_start)) {
        rememberRunMessageSent(history.cartId, 'message_start', history.message_start!)
      }
      if (isRunMessageFilled(history.message_on_the_way)) {
        rememberRunMessageSent(history.cartId, 'message_on_the_way', history.message_on_the_way!)
      }
      if (isRunMessageFilled(history.message_arriving_on_location)) {
        rememberRunMessageSent(
          history.cartId,
          'message_arriving_on_location',
          history.message_arriving_on_location!
        )
      }
      if (isRunMessageFilled(history.message_arrived_on_location)) {
        rememberRunMessageSent(
          history.cartId,
          'message_arrived_on_location',
          history.message_arrived_on_location!
        )
      }
    }

    return histories.map((history) => {
      const localMarks = runMessagesSentAtRef.current.get(history.cartId)
      if (!localMarks) return history

      return {
        ...history,
        message_start: isRunMessageFilled(history.message_start)
          ? history.message_start
          : localMarks.message_start ?? null,
        message_on_the_way: isRunMessageFilled(history.message_on_the_way)
          ? history.message_on_the_way
          : localMarks.message_on_the_way ?? null,
        message_arriving_on_location: isRunMessageFilled(history.message_arriving_on_location)
          ? history.message_arriving_on_location
          : localMarks.message_arriving_on_location ?? null,
        message_arrived_on_location: isRunMessageFilled(history.message_arrived_on_location)
          ? history.message_arrived_on_location
          : localMarks.message_arrived_on_location ?? null,
      }
    })
  }

  function markRunMessage(payload: { type: RunMessageType; cartId: number }): void {
    if (!claimRunMessage(payload.cartId, payload.type)) return

    void api.patch('/api/v3/motoboy/runs/message-from-run', payload).catch((error) => {
      releaseRunMessageClaim(payload.cartId, payload.type)
      console.error('Não foi possível marcar a mensagem da corrida:', error)
    })
  }

  function sendRunMessage(
    message: string,
    number: string,
    payload: { type: RunMessageType; cartId: number }
  ): void {
    if (!claimRunMessage(payload.cartId, payload.type)) return

    void sendMessageFromRun(message, number, payload).catch((error) => {
      releaseRunMessageClaim(payload.cartId, payload.type)
      console.error('Não foi possível enviar a mensagem da corrida:', error)
    })
  }

  /**
   * Envia as mensagens da corrida conforme o status de cada item do change_history:
   * - PENDING: recebe apenas message_start.
   * - RUNNING: message_start é somente marcada (sem envio) e message_on_the_way é enviada.
   *
   * Quando um pedido PENDING passa para RUNNING, o message_start já está marcado,
   * então apenas o message_on_the_way é disparado.
   */
  function processRunMessages(
    changeHistories: ChangeHistory[],
    location: RunnerLocationTransmitPayload
  ): void {
    if (!changeHistories.length) return

    const pendingCount = changeHistories.filter(
      (history) => history.status === 'PENDING'
    ).length
    const routeDescription =
      pendingCount > 0
        ? ` junto com mais ${pendingCount} ${pendingCount === 1 ? 'pedido' : 'pedidos'}`
        : ''

    for (const history of changeHistories) {
      const cart =
        cartsRef.current.find((currentCart) => currentCart.id === history.cartId) ??
        (location.carts ?? []).find((locationCart) => locationCart.id === history.cartId)

      if (!cart?.client?.whatsapp) continue

      if (history.status === 'RUNNING') {
        if (!isRunMessageFilled(history.message_start)) {
          markRunMessage({ type: 'message_start', cartId: history.cartId })
        }
        if (!isRunMessageFilled(history.message_on_the_way)) {
          const trackingUrl = getCartStatusUrl(cart.code as string)
          sendRunMessage(
            `Quase lá! Nosso entregador está indo diretamente para você.\nRASTREIE SEU PEDIDO PELO LINK: ${trackingUrl}`,
            cart.client.whatsapp,
            { type: 'message_on_the_way', cartId: history.cartId }
          )
        }
        continue
      }

      if (history.status === 'PENDING' && !isRunMessageFilled(history.message_start)) {
        sendRunMessage(
          `🚀 Seu pedido WM-${cart.code ?? cart.id} já saiu para entrega! Ele está na rota${routeDescription}. Em breve será a sua vez.`,
          cart.client.whatsapp,
          { type: 'message_start', cartId: history.cartId }
        )
      }
    }
  }

  useEffect(() => {
    runnerLocationHandlerRef.current = handleRunnerLocationUpdate
    return () => {
      runnerLocationHandlerRef.current = null
    }
  })

  useEffect(() => {
    if (result_carts) {
      const { carts } = result_carts
      setCarts(carts.map((cart: CartType) => new Cart(cart)))
      setShowLostRequestsModal(carts.length > 3)
    }
  }, [result_carts])

  useEffect(() => {
    if (result_motoboys) {
      const { motoboys } = result_motoboys
      setMotoboys(motoboys)
    }
  }, [result_motoboys])

  useEffect(() => {
    if (result_package_data) {
      const { packageCarts } = result_package_data
      setPackageCarts(packageCarts)
    }
  }, [result_package_data])

  const sendCartForMottu = async (cartsMottu: CartType | Cart) => {
    const cartsIds = [cartsMottu.id]

    try {
      const { data } = await api.post('/dashboard/mottu/create', { cartsIds })

      handleShowToast({
        type: 'success',
        content: 'Entrega criada com sucesso',
        title: 'Entrega',
      })
      return data.carts
    } catch (error) {
      handleShowToast({
        type: 'erro',
        content: 'Falha ao criar entrega',
        title: 'Entrega',
      })
      throw new Error('Não foi possível enviar pedido para Mottu')
    }
  }

  const playIncomingCartSound = () => {
    if (audio) {
      audio?.load()
      audio?.play().catch(() => {
        console.error('Não foi possível reproduzir o som.')
      })
    }
  }

  const normalizeRequiredDateToSql = (value: string) => {
    if (DateTime.fromSQL(value).isValid) {
      return value
    }

    const parsedIso = DateTime.fromISO(value, { setZone: true })
    if (parsedIso.isValid) {
      return parsedIso.toFormat('yyyy-MM-dd HH:mm:ss')
    }

    return value
  }

  const normalizeOptionalDateToSql = (value?: string | null) => {
    if (!value || typeof value !== 'string') {
      return value
    }

    if (DateTime.fromSQL(value).isValid) {
      return value
    }

    const parsedIso = DateTime.fromISO(value, { setZone: true })
    if (parsedIso.isValid) {
      return parsedIso.toFormat('yyyy-MM-dd HH:mm:ss')
    }

    return value
  }

  const normalizeCartRealtimeDates = (cartData: any) => {
    const realtimeStatusPayment =
      cartData.statusPayment ??
      cartData.status_payment ??
      (cartData.controls?.stripe?.status === 'paid' ? 'paid' : undefined) ??
      (cartData.controls?.asaas?.status === 'RECEIVED' ? 'paid' : undefined)

    const normalized = {
      ...cartData,
      ...(realtimeStatusPayment
        ? { statusPayment: realtimeStatusPayment }
        : {}),
      created_at: normalizeRequiredDateToSql(cartData.created_at),
      updated_at: normalizeRequiredDateToSql(cartData.updated_at),
      packageDate: normalizeOptionalDateToSql(cartData.packageDate),
      paid_at: normalizeOptionalDateToSql(cartData.paid_at),
    }

    // Normaliza datas de objetos aninhados se existirem
    if (normalized.client?.created_at) {
      normalized.client = {
        ...normalized.client,
        created_at: normalizeOptionalDateToSql(normalized.client.created_at),
        updated_at: normalizeOptionalDateToSql(normalized.client.updated_at),
      }
    }

    if (normalized.address?.created_at) {
      normalized.address = {
        ...normalized.address,
        created_at: normalizeOptionalDateToSql(normalized.address.created_at),
        updated_at: normalizeOptionalDateToSql(normalized.address.updated_at),
      }
    }

    if (normalized.cupom?.created_at) {
      normalized.cupom = {
        ...normalized.cupom,
        created_at: normalizeOptionalDateToSql(normalized.cupom.created_at),
        updated_at: normalizeOptionalDateToSql(normalized.cupom.updated_at),
      }
    }

    return normalized as CartType
  }

  const handleIncomingCart = async (incomingCart: CartType | Cart) => {
    let wsCart = incomingCart

    DateTime.local()
      .setZone(profile.timeZone)
      .toFormat('yyyy-MM-dd HH:mm:ss')
      .trim()

    verifyInventory()

    if (
      profile.options?.integrations?.mottu?.callAutomatic &&
      (wsCart as any).paid_at
    ) {
      const cartWitchMottu = await sendCartForMottu(wsCart)
      wsCart = cartWitchMottu[0]
    }

    addItemCart(wsCart)
    onCart(wsCart)
    setCart(new Cart(wsCart))

    switch (wsCart.type) {
      case 'P':
        const audioPackage = profile.options.playSoundPackage
          ? playIncomingCartSound()
          : false
        cartEvents.emit('newPackage', audioPackage)
        break
      case 'T':
        const audioTable = profile.options.playSoundPdv
          ? playIncomingCartSound()
          : false
        cartEvents.emit('newCartTable', audioTable)
        break
      case 'L':
        const audioEatOnSite = profile.options.playSoundEatOnSite
          ? playIncomingCartSound()
          : false
        cartEvents.emit('newCartEatOnSite', audioEatOnSite)
        break
      default:
        const audioDelivery = profile.options.playSoundDelivery
          ? playIncomingCartSound()
          : false
        cartEvents.emit('newCart', audioDelivery, wsCart)
        break
    }
  }

  useEffect(() => {
    if (profile.id) {
      wsConnect(
        {
          url: `${process.env.NODE_ENV === 'development' ? 'ws' : 'wss'}://${process.env.NEXT_PUBLIC_WS_SOCKET_API}/adonis-ws`,
          attemptsInterval: 1000 * 10,
          reconnectAttempts: 10,
        },
        () => {
          wsSubscribe('profile', profile.slug, (subscription) => {
            subscription.on(`profile`, ({ data }) => {
              if (data.forceLogout) {
                signOut()
              }
            })
          })
          wsSubscribe('request', profile.slug, (subscription) => {
            subscription.on('request', async (wsCart) => {
              const normalizedCart = normalizeCartRealtimeDates(wsCart)
              await handleIncomingCart(normalizedCart)
            })
          })
          wsSubscribe('command', profile.slug, (subscription) => {
            subscription.on('command', (command) => {
              setFinishCommand(command.finish)
              setSocketCommands((prev: any) => [...prev, command])
            })
          })
          if (profile.options?.print.app) {
            wsSubscribe('print', profile.slug, (subscription) => {
              setPrintSubscription(subscription)
              setWsPrint(subscription)
            })
          }
        },
        onWsReconnect
      )
    }
  }, [])

  useEffect(() => {
    if (printSubscription) {
      printSubscription.on(
        'sucessesFullPrinting',
        ({ requestId }: { requestId: number }) => {
          const cartIndex = state.carts.findIndex((r) => r.id === requestId)
          if (cartIndex !== -1) {
            state.carts[cartIndex].print = 1
          }
          setCarts([...state.carts])
        }
      )
    }
  }, [state.carts, printSubscription])
  //
  let alreadyCallOrdersData = false
  useEffect(() => {
    if (profile?.id && profile?.options?.integrations?.ifood && !alreadyCallOrdersData) {
      if ('isElectron' in window) {
        socketIoConnect({ url: 'ws://127.0.0.1:3434' })
      }
      api.get('/dashboard/ifood/ordersData').then(({ data: orders }) => {
        const ordersIfood = orders.filter((order: any) => order.additionalInfo?.salesChannel !== 'POS')
        orders = ordersIfood?.map((order: IfoodOrderType) =>
          Cart.fromIfood({ order, profile })
        )
        let ordersPlaced = orders.filter(
          (order: any) => order.ifoodStatus === 'PLACED'
        )
        let processedOrders = orders.filter(
          (order: any) => order.ifoodStatus !== 'PLACED'
        )

        if (ordersPlaced.length > 0) {
          setModalNewPlacedIfood(true)
        }
        setNewPlacedOrders({ orders: ordersPlaced })
        setIfoodOrders({ orders: processedOrders })
      })
      alreadyCallOrdersData = true
    }
  }, [profile?.options?.integrations?.ifood])

  socket.on('newOrderIfood', (data: any) => {
    setNewPlacedOrders((prev) => ({ ...prev, orders: [...prev.orders, data] }))
    setModalNewPlacedIfood(true)
  })


  useEffect(() => {
    if (
      window.DesktopApi &&
      typeof (window.DesktopApi as any).integrationIfoodAlter === 'function'
    ) {
      ; (window.DesktopApi as any).integrationIfoodAlter(
        (event: any, data: any) => {
          if (profile?.options?.integrations?.ifood) {
            window.location.reload()
          }
        }
      )
    }
  }, [])

  socket.on('processedOrderIfood', (data) => {
    if (!!data?.controls) {
      data.created_at = DateTime.fromISO(data.created_at).toFormat('yyyy-MM-dd HH:mm:ss')
      const newCart = new Cart({ ...data })
      setCart(newCart)
      return
    }
    if (data.additionalInfo?.salesChannel === 'POS') {
      return
    }
    if (!data.additionalInfo?.print) {
      setRequestsToPrint({
        carts: [
          Cart.fromIfood({
            order: data,
            profile,
          }),
        ],
        show: false,
        command: null,
        directPrint: true,
      })
    }
    const repeatedId = ifoodOrders.orders.find((order) => order.id === data.id)

    if (!repeatedId) {
      data.additionalInfo.print = 1
      data = Cart.fromIfood({ order: data, profile })
      setIfoodOrders((prev) => ({ ...prev, orders: [...prev.orders, data] }))
    }
  })

  useEffect(() => {
    const idsOrders = new Set(ifoodOrders.orders?.map((order) => order.id))
    const attNewPlacedOrders = newPlacedOrders.orders?.filter(
      (order: any) => !idsOrders.has(order.id)
    )
    setNewPlacedOrders((prev) => ({ ...prev, orders: [...attNewPlacedOrders] }))
  }, [ifoodOrders])

  useEffect(() => {
    if (profile?.options?.integrations?.agilizone?.auto_call) {
      const cart = state.carts
        .toReversed()
        .filter((c) => !Boolean(c.controls.agilizone) && c.addressId)[0]
      if (cart) {
        setTimeout(() => {
          createOrderAgilizone({ cart })
        }, 1000 * 2)
      }
    }
    async function pollingAgilizone() {
      if (
        profile?.options?.integrations?.agilizone &&
        state.carts.some((cart) => cart.controls?.agilizone !== undefined) &&
        state.carts.some((cart) => cart.status !== 'delivered') &&
        state.carts.some((cart) => cart.status !== 'canceled')
      ) {
        try {
          const agilizone = new AgilizoneService({
            client_id: profile?.options?.integrations?.agilizone?.client_id,
            client_secret:
              profile?.options?.integrations?.agilizone?.client_secret,
            baseURL: process.env.NEXT_PUBLIC_AGILIZONE_URL!,
          })

          const { orders } = await agilizone.getOrders({
            page: 1,
            pageSize: 100,
          })
          const cartsWithAgilizone = state.carts.filter(
            (cart) => cart.controls.agilizone
          )
          const updatedCarts = cartsWithAgilizone
            .map((cart) => {
              const order = orders.find(
                (order: any) => order._id === cart.controls.agilizone?._id
              )

              const oldStatus = cart.status

              if (
                order &&
                order.status !== cart.controls.agilizone?.status &&
                cart.status !== 'delivered' &&
                cart.status !== 'canceled'
              ) {
                switch (order.status) {
                  case 'PREPARED':
                    cart.status = 'production'
                    break
                  case 'COLLECTED':
                    cart.status = 'transport'
                    break
                  case 'COMPLETED':
                    cart.status = 'transport'
                    // cart.status = 'delivered'
                    break
                  case 'CANCELED':
                    cart.status = 'canceled'
                    break
                }
                if (oldStatus !== cart.status) {
                  return { id: cart.id, status: cart.status }
                }
              }
            })
            .filter(Boolean)

          if (updatedCarts.length) {
            const { data } = await api.patch('dashboard/carts/update/status', {
              carts: updatedCarts,
            })

            const carts_agil = state.carts.map((cart) => {
              const updatedCart = data.carts.find(
                (c: CartType) => c.id === cart.id
              )
              if (updatedCart) {
                cart.status = updatedCart.status
              }
              return new Cart(cart)
            })
            carts_agil.forEach((cart) => {
              setCart(cart)
            })
          }
        } catch (error) {
          console.error(error)
        }
        // setTimeout(pollingAgilizone, 45000)
      }
    }
    const pollingInterval = setInterval(() => {
      pollingAgilizone()
    }, 1000 * 45)

    return () => {
      clearInterval(pollingInterval)
    }
  }, [state.carts])
  useEffect(() => {
    // só roda quando o profile for carregado
    if (!profile?.id) return

    // verifica se o lojista tem o alerta ativo
    const hasKeetaAlert = Boolean(profile?.options?.keetaAlert)
    const alertAlreadyShown = localStorage.getItem(
      'keeta_integration_alert_shown'
    )

    if (hasKeetaAlert && !alertAlreadyShown) {
      setShowKeetaModal(true)
    }
  }, [profile])

  useEffect(() => {
    if (profile && connections && profile?.options?.integrations?.keeta) {
      if (!connections.get('keeta')) {
        createConnection({
          options: { baseUrl: process.env.NEXT_PUBLIC_KEETA_API_URL! },
          connectionID: 'keeta',
        })
      }
      createSubscription({
        connectionID: 'keeta',
        channel: `profile:${profile.options?.integrations?.keeta?.merchant?.id}`,
      })
        .then(({ subscription }) => {
          if (subscription) {
          subscription.onMessage(async(message: any) => {
            switch (message.event) {
              case 'order:created':
                setKeetaOrders((prev) => {
                  if (
                    prev.some((order) => order.id === message.data.order.id)
                  ) {
                    return prev
                  }
                  const cart = Cart.fromKeeta({
                    order: message.data.order,
                    profile: profile as any,
                    customer: message.data.order.customer,
                    items: message.data.order.items,
                  })
                  setRequestsToPrint({ carts: [cart], show: false, command: null, directPrint: true })
                  return [
                    Cart.fromKeeta({
                      order: message.data.order,
                      profile: profile as any,
                      customer: message.data.order.customer,
                      items: message.data.order.items,
                    }),
                    ...prev,
                  ].toSorted((a, b) => b.id - a.id)
                })
                
                playIncomingCartSound()
                break
                case 'order:cancelled':
                  const cart = Cart.fromKeeta({
                          order: message.data.order,
                          profile: profile as any,
                          customer: message.data.order.customer,
                          items: message.data.order.items,
                        })
                await api.post(`/dashboard/keeta/addCashier`, { cart, cartTotal: cart.getTotalValue('total') })
                  break
                  default:
                    if(message.event === 'order:confirmed') {
                  const cart = Cart.fromKeeta({
                          order: message.data.order,
                          profile: profile as any,
                          customer: message.data.order.customer,
                          items: message.data.order.items,
                        })
                        setKeetaOrders((prev) => {
                  if (
                    prev.some((order) => order.id === message.data.order.id)
                  ) {
                    return prev
                  }
                  return [
                    cart,
                    ...prev,
                  ].toSorted((a, b) => b.id - a.id)
                })
                setRequestsToPrint({ carts: [cart], show: false, command: null, directPrint: true })
                await api.post(`/dashboard/keeta/addCashier`, { cart })
                }
                setKeetaOrders((prev) => {
                  return prev.map((o) => {
                    if (o.id === message.data.order.id) {
                      return Cart.fromKeeta({
                        order: message.data.order,
                        profile: profile as any,
                        customer: message.data.order.customer,
                        items: message.data.order.items,
                      })
                    }
                    return o
                  }).toSorted((a, b) => b.id - a.id)
                })
                break
            }
          })
        }
        if (!subscription) {
          console.error('Keeta subscription not found')
        }
        })
        .catch(console.error)
    }
    if (profile && connections) {
      const connection = connections.get('whatsmenu')
      if (connection) {
        connection.subscription(`profile:${profile.slug}`).onMessage(async (message: any) => {
          // Localização da corrida já é tratada via runnerLocationHandlerRef no app.ctx
          if (parseRunnerLocationMessage(message)) {
            return
          }

          const { data, event } = message ?? {}
          switch (event) {
              case 'cart:paid':
                if (!profile?.options?.integrations?.stripe) {
                  break
                }

                const normalizedCart = normalizeCartRealtimeDates(data.cart)
                normalizedCart.statusPayment =
                  normalizedCart.statusPayment ?? 'paid'

                const existingCart = state.carts.find((cart) => cart.id === normalizedCart.id)

                if (existingCart) {
                  setCart(
                    new Cart({
                      ...existingCart,
                      ...normalizedCart,
                      taxDelivery:
                        normalizedCart.taxDelivery !== undefined
                          ? Number(normalizedCart.taxDelivery)
                          : existingCart.taxDelivery,
                    })
                  )
                  break
                }

                await handleIncomingCart(new Cart({ ...normalizedCart }))
              break
            case `cart:update`:
              if (!profile?.options?.integrations?.entregasexpressas) {
                break
              }

              const cart = state.carts.find(c => c.id === data.cart.id)
              if (cart) {
                setCart(new Cart({ ...cart, ...data.cart, taxDelivery: Number(data.cart.taxDelivery) }))
              }
              break
            case 'cart:grovenfe': {
              const cartGrovenfe = cartsRef.current.find(c => c.id === data.cart.id)
              if (cartGrovenfe) {
                setCart(new Cart({
                  ...cartGrovenfe,
                  controls: { ...cartGrovenfe.controls, grovenfe: data.cart.controls?.grovenfe },
                }))
              }
              break
            }
            case 'cart:itensUpdated': {
              const cart = state.carts.find(c => c.id === data.cart.id)
              if (cart) {
                setCart(new Cart({ ...cart, ...data.cart, taxDelivery: Number(data.cart.taxDelivery) }))
              }
              break
            }
          }
        })
      }
    }

    // if (profile && connections && profile?.options?.integrations?.nineNineFood) {
    if (profile && connections && profile?.options?.integrations?.nine_nine_food?.store) {
      console.log('Connecting to NineNine WebSocket...')
      if (!connections.get('nine-nine')) {
        createConnection({
          options: { baseUrl: process.env.NEXT_PUBLIC_NINE_NINE_API_URL! },
          connectionID: 'nine-nine',
        })
      }
      createSubscription({
        connectionID: 'nine-nine',
        // channel: `profile:${profile.options?.integrations?.nineNineFood?.store?.id}`,
        channel: `profile:${profile.options?.integrations?.nine_nine_food.store.id}`,
      })
        .then(({ subscription }) => {
          if (!subscription) {
            console.error('Failed to create subscription for NineNine updates')
            return
          }
          subscription.onMessage(async (message: any) => {
            console.log('NineNine message:', message.data)
            switch (message.event) {
              case 'order:new': {
                const incomingOrderId = String(message.data.order.order_id)
                const newCart = Cart.fromNineNine({
                  order: message.data.order,
                  profile: profile as any,
                  customer: message.data.order.customer,
                  items: message.data.order.items,
                })
                let isNewOrder = false

                setNineNineOrders((prev) => {
                  const existingOrderIndex = prev.findIndex((o) => o.orderId === incomingOrderId)

                  if (existingOrderIndex !== -1) {
                    const updated = [...prev]
                    updated[existingOrderIndex] = newCart
                    return updated.toSorted((a, b) => Number(b.code) - Number(a.code))
                  }

                  // isNewOrder = true

                  // if (isNewOrder) {
                  //   setRequestsToPrint({ carts: [newCart], show: false, command: null, directPrint: true })
                  // }
                  setRequestsToPrint({ carts: [newCart], show: false, command: null, directPrint: true })

                  return [newCart, ...prev].toSorted((a, b) => Number(b.code) - Number(a.code))
                })

                if (isNewOrder && profile.options.playSoundDelivery) {
                  playIncomingCartSound()
                }
                await api.post(`/dashboard/99food/addCashier`, { cart: newCart, cartTotal: newCart.getTotalValue('total') })
                break
              }
              default:
              break;
            }
          })
        })
        .catch(console.error)
    }
  }, [profile, connections.size])

  useEffect(() => {
    if (profile.options?.integrations?.keeta?.merchant?.id) {
      keetaIntegratorApi
        .post('/orders/list', {
          merchantId: profile.options?.integrations?.keeta?.merchant.id,
          timezone: profile.timeZone,
        })
        .then(({ data }) => {
          const orders = [...new Map(
            data.orders.map((order: any) => [order.display_id, order])
          ).values()];
          setKeetaOrders(
            orders.map((order: any) =>
              Cart.fromKeeta({
                order,
                profile: profile as any,
                customer: order.customer,
                items: order.items,
              })
            )
          )
        })
        .catch(console.error)
    }
    if (profile.options?.integrations?.nine_nine_food?.store?.id) {
      nineNineIntegratorApi
        .post('/orders/list', {
          store_id: profile?.options?.integrations?.nine_nine_food?.store?.id,
          timezone: profile.timeZone,
        })
        .then(({ data }) => {
          setNineNineOrders(
            data.orders.map((order: NineNineOrderInfoType) =>
              Cart.fromNineNine({
                order,
                profile: profile as any,
                customer: order.receive_address as any,
                items: order.items as any,
              })
            )
          )
        })
        .catch(console.error)
    }
  }, [])

  if (!profile.id) {
    return (
      <CartsContext.Provider
        value={{
          setKeetaOrders,
          carts: [],
          packageCarts: {
            data: [],
            lastPage: 1,
            page: 1,
            perPage: 30,
            total: 0,
          },
          setCarts: () => { },
          setPackageCarts: () => { },
          setShowLostRequestsModal: () => { },
          showLostRequestsModal: false,
          cartEvents,
          setCart: () => { },
          setMotoboys,
          motoboys: [],
          updateMotoboyId: () => { },
          createRun: async () => undefined,
          ifoodOrders,
          keetaOrders,
          setIfoodOrders,
          modalNewPlacedIfood,
          setModalNewPlacedIfood,
          newPlacedOrders,
          setNewPlacedOrders,
          sendToMotoboy,
          setSendToMotoboy,
          nineNineOrders,
          setNineNineOrders,
          createOrderAgilizone: async () => { },
          checkAvailabilityMotoboyIfood: async () => { },
          createOrderEntregasExpressas: async () => { return { result: null } },
          sendAppLinkToMotoboy: () => { },
          trackfyPendencies: [],
          hasTrackfyPendencies: false,
          getMotoboyTrackfyPendencies,
          hasMotoboyTrackfyPendencies,
        }}
      >
        {children}
      </CartsContext.Provider>
    )
  }

  return (
    <CartsContext.Provider
      value={{
        carts: state.carts,
        packageCarts: state.packageCarts,
        setCarts,
        setCart,
        updateMotoboyId,
        createRun,
        setPackageCarts,
        showLostRequestsModal,
        setShowLostRequestsModal,
        motoboys,
        setMotoboys,
        cartEvents,
        ifoodOrders,
        keetaOrders,
        setIfoodOrders,
        modalNewPlacedIfood,
        setModalNewPlacedIfood,
        newPlacedOrders,
        setNewPlacedOrders,
        sendToMotoboy,
        setSendToMotoboy,
        createOrderAgilizone,
        setKeetaOrders,
        checkAvailabilityMotoboyIfood,
        createOrderEntregasExpressas,
        nineNineOrders,
        setNineNineOrders,
        sendAppLinkToMotoboy,
        trackfyPendencies,
        hasTrackfyPendencies,
        getMotoboyTrackfyPendencies,
        hasMotoboyTrackfyPendencies,
      }}
    >
      <>{children}</>
      <ModalIfoodNewOrders show={modalNewPlacedIfood} />
      <KeetaOrderModal />
      <NineNineOrderModal />
      {showKeetaModal && (
        <KeetaIntegrationAlertModal
          isOpen={showKeetaModal}
          onClose={() => {
            localStorage.setItem('keeta_integration_alert_shown', 'true')
            setShowKeetaModal(false)
          }}
        />
      )}
    </CartsContext.Provider>
  )
}
